import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Package, MapPin, Users, Euro, Building, TrendingUp, Truck, Leaf } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article1 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-blue-600 text-white mr-4">
              <Package className="h-3 w-3 mr-1" />
              Logistics Investment
            </Badge>
            <span className="text-sm text-gray-600">Capital.fr</span>
          </div>

          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>

          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.1.title')}
          </h1>

          <img
            src="https://www.capital.fr/imgre/fit/~1~cap~2026~04~15~6ed3127c-8f31-4e34-a6e5-a9705b8edbd7.jpeg/630x354/quality/63/crop-from/center/focus-point/50%2C50/image.avif"
            alt="Amazon Distribution Centre France"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.1.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4">
                {language === 'ko' ? '주요 수치' : 'Key Figures'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Euro className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '투자액' : 'Investment'}
                  </h4>
                  <p className="text-2xl font-bold text-blue-600">€250M</p>
                  <p className="text-xs">{language === 'ko' ? '신규 시설' : 'New facility'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Users className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '신규 일자리' : 'New Jobs'}
                  </h4>
                  <p className="text-2xl font-bold text-green-600">2,000</p>
                  <p className="text-xs">{language === 'ko' ? '정규직' : 'Permanent contracts'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Building className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '총 면적' : 'Total Floor Area'}
                  </h4>
                  <p className="text-2xl font-bold text-purple-600">189,000 m²</p>
                  <p className="text-xs">{language === 'ko' ? '3개 층' : '3 levels'}</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.1.section1.title')}
            </h2>
            <p className="mb-6">{t('article.1.p1')}</p>
            <p className="mb-6">{t('article.1.p2')}</p>

            {/* Location highlight */}
            <div className="bg-green-50 p-6 rounded-lg my-8 border border-green-200">
              <h3 className="font-bold text-green-900 mb-4 flex items-center">
                <MapPin className="mr-2 h-5 w-5" />
                {language === 'ko' ? '시설 세부 정보' : 'Facility Details'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-green-800">
                <div>
                  <ul className="space-y-2">
                    <li>📍 <strong>{language === 'ko' ? '위치:' : 'Location:'}</strong> Ensisheim, Haut-Rhin, Grand Est</li>
                    <li>📐 <strong>{language === 'ko' ? '건축 면적:' : 'Footprint:'}</strong> ~60,000 m²</li>
                    <li>🏢 <strong>{language === 'ko' ? '연면적:' : 'Total area:'}</strong> 189,000 m²</li>
                    <li>🏗️ <strong>{language === 'ko' ? '층수:' : 'Floors:'}</strong> 3 {language === 'ko' ? '개 층' : 'levels'}</li>
                  </ul>
                </div>
                <div>
                  <ul className="space-y-2">
                    <li>💰 <strong>{language === 'ko' ? '투자액:' : 'Investment:'}</strong> €250 million</li>
                    <li>👷 <strong>{language === 'ko' ? '신규 일자리:' : 'New jobs:'}</strong> 2,000</li>
                    <li>📅 <strong>{language === 'ko' ? '개장 예정:' : 'Opening:'}</strong> {language === 'ko' ? '2027년 말' : 'End of 2027'}</li>
                    <li>📋 <strong>{language === 'ko' ? '고용 형태:' : 'Contract type:'}</strong> CDI ({language === 'ko' ? '정규직' : 'Permanent'})</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.1.section2.title')}
            </h2>
            <p className="mb-6">{t('article.1.p3')}</p>
            <p className="mb-6">{t('article.1.p4')}</p>

            {/* SME impact box */}
            <div className="bg-yellow-50 p-6 rounded-lg my-8 border border-yellow-200">
              <h3 className="font-bold text-yellow-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                {language === 'ko' ? '그랑 에스트 중소기업 성과 (2024)' : 'Grand Est SME Performance (2024)'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-yellow-800">
                <div>
                  <p className="text-2xl font-bold text-yellow-700">3 {language === 'ko' ? '개/분' : 'products/min'}</p>
                  <p className="text-xs">{language === 'ko' ? 'Amazon.fr에서 판매된 제품' : 'Products sold on Amazon.fr'}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-700">€57M+</p>
                  <p className="text-xs">{language === 'ko' ? '수출 매출' : 'Export sales'}</p>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.1.section3.title')}
            </h2>
            <p className="mb-6">{t('article.1.p5')}</p>
            <p className="mb-6">{t('article.1.p6')}</p>

            {/* Investment timeline */}
            <div className="bg-purple-50 p-6 rounded-lg my-8 border border-purple-200">
              <h3 className="font-bold text-purple-900 mb-4">
                {language === 'ko' ? '그랑 에스트 아마존 투자 현황' : 'Amazon in Grand Est — At a Glance'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-purple-800">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">€1.5B+</p>
                  <p className="text-xs">{language === 'ko' ? '2010년 이후 누적 투자' : 'Invested since 2010'}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">4,000+</p>
                  <p className="text-xs">{language === 'ko' ? '현재 정규직 직원' : 'Current permanent employees'}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">25+</p>
                  <p className="text-xs">{language === 'ko' ? '지역 내 운영 연수' : 'Years operating in region'}</p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.1.section4.title')}
            </h2>
            <p className="mb-6">{t('article.1.p7')}</p>
            <p className="mb-6">{t('article.1.p8')}</p>

            {/* Environmental & logistics benefits */}
            <div className="bg-teal-50 p-6 rounded-lg my-8 border border-teal-200">
              <h3 className="font-bold text-teal-900 mb-4 flex items-center">
                <Truck className="mr-2 h-5 w-5" />
                {language === 'ko' ? '물류 및 환경 이점' : 'Logistics & Environmental Benefits'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-teal-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Truck className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '배송' : 'Delivery'}
                  </h4>
                  <ul className="space-y-1">
                    <li>• {language === 'ko' ? '배송 시간 단축' : 'Reduced delivery times'}</li>
                    <li>• {language === 'ko' ? '향상된 라스트마일 효율' : 'Improved last-mile efficiency'}</li>
                    <li>• {language === 'ko' ? '지역 주문 처리 역량 강화' : 'Stronger regional fulfilment capacity'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Leaf className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '환경' : 'Environment'}
                  </h4>
                  <ul className="space-y-1">
                    <li>• {language === 'ko' ? '운송 관련 CO2 배출량 감소' : 'Lower CO2 from transportation'}</li>
                    <li>• {language === 'ko' ? '최신 에너지 효율 설계' : 'Modern energy-efficient design'}</li>
                    <li>• {language === 'ko' ? '첨단 로보틱스 및 AI 재고 관리' : 'Advanced robotics & AI inventory'}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Key takeaways */}
            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '주요 시사점' : 'Key Takeaways'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '아마존이 프랑스 오랭(엔시스하임)에 2억5천만 유로 규모의 신규 물류 센터 건설 발표' : 'Amazon announces €250M new distribution centre in Ensisheim, Haut-Rhin, France'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '189,000 m² 규모, 3개 층 — 2027년 말 개장 예정' : '189,000 m² across 3 levels — opening planned by end of 2027'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '그랑 에스트 지역에 2,000개의 정규직 일자리 창출' : '2,000 permanent jobs to be created in the Grand Est region'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '아마존의 그랑 에스트 누적 투자액 15억 유로 이상 (2010년 이후), 4,000명 이상 정규직 고용' : 'Amazon has invested €1.5B+ in Grand Est since 2010, employing 4,000+ on permanent contracts'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '배송 시간 단축 및 운송 CO2 배출 감소를 목표로 함' : 'Aims to reduce delivery times and lower CO2 emissions from transportation'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '그랑 에스트 중소기업의 2024년 Amazon.fr 수출 매출 5,700만 유로 이상 달성' : 'Grand Est SMEs on Amazon.fr achieved €57M+ in export sales in 2024'}</span></li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Capital.fr — Amazon annonce la construction d'un nouveau centre de distribution en France.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://www.capital.fr/entreprises-marches/amazon-annonce-la-construction-d-un-nouveau-centre-de-distribution-en-france-un-investissement-de-plus-de-250-millions-d-euros-1525631"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article1;
